<template>
  <v-container fluid class="px-0 pb-0">
    <v-row dense>
      <v-col v-for="f in filterTypes" :key="f.key" cols="12" md="6" lg="4" style="min-width:260px">
        <v-select
          :prepend-inner-icon="IconHelpers.getIconForObjectType(f.key)"
          clearable
          :items="filterItems[f.key]"
          :value="filterItems[f.key]?.length === 1 ? filterItems[f.key][0] : f.val"
          return-object
          @input="setFilter(f.key, $event)"
          outlined
          rounded
          dense
          :label="$t(f.key)"
          class="mb-n6 mr-2"
          :id="`ticketPostingFilter${$t(f.key)}`"
        >
          <template #item="{ item, on }">
            <v-list-item v-on="on">
              <span>{{item}}</span>
            </v-list-item>
          </template>
          <template #selection="{ item } ">
            <template>
              <span>{{item}}</span>
            </template>
          </template>
        </v-select>
      </v-col>
    </v-row>

    <v-col cols="12" class="pa-0 mt-n2">
      <DataTable
      :items="itemsFiltered || items"
      :headers="headers"
      :selected.sync="selectedItemsModel"
      :tableKey="tableKey"
      :showSelect="showSelect"
      :keepSelectionWhenItemsChange="filterTypes != undefined"
      :customCells="customCells"
      :allSelected="allSelected"
      @select-all-clicked="selectAllClicked"
      hideDefaultSelectAll
      :propSelectAllTooltipText="selectAllTooltipText"
      :tableHeightCalculationData="{ offset: 500 }"
      @search-changed="searchChanged">
        <template
        v-for="slot in customCells"
        v-slot:[`${slot.value}`]="{item}"
        >
          <slot :name="`${slot.slotName}`" :item="item"/>
        </template>

        <template #header.data-table-select v-if="!hasSearchText">
          <Icon
          top
          :icon="iconForSelectAllBox"
          :iconColor="allSelected || allFilteredSelected ? 'success' : 'rgba(0,0,0,0.3)'"
          :tooltipText="selectAllTooltipText"
          tooltipColor="secondary"
          :small="false"
          @icon-clicked="selectAllClicked"
          :disabled="selectAllDisabled"
          :style="selectAllStyle"
          />
        </template>
        <template #footer.prepend v-if="showFilterInfoPrependedFooter">
          <v-chip :color="allSelected ? 'success' : ''">
            <span>
              {{ `${$t('selected')}: ${selectedItemsModel.length} / ${(items).length}` }}
            </span>
          </v-chip>

          <v-chip :color="allFilteredSelected ? 'success' : ''" class="ml-1" v-if="showFilterIndicatorChip">
            <v-row no-gutters align="center">
              <v-col>
                <span v-for="(f, index) in selectedFilterIndicators" :key="f.key" no-gutters align="center" class="flex-nowrap">
                  <v-icon x-small class="mb-1" :color="allFilteredSelected ? 'white' : 'secondary'">{{ IconHelpers.getIconForObjectType(f.key) }}</v-icon>
                  <span v-if="$vuetify.breakpoint.lgAndUp" class="ml-1">{{ $vuetify.breakpoint.xl || selectedFilterIndicators.length < 2 ? f.value : truncate(f.value, { length: 13 }) }}</span>
                  <span v-if="index < selectedFilterIndicators.length - 1 && $vuetify.breakpoint.lgAndUp" class="pr-2">,</span>
                </span>
              </v-col>

              <v-col>
                <span>{{ `: ${intersection(selectedItemsModel, itemsFiltered).length} / ${(itemsFiltered).length}` }}</span>
              </v-col>
            </v-row>
          </v-chip>
        </template>

        <template #footer v-else-if="showFilterInfoReplaceFooter">
          <v-container fluid>
            <v-chip :color="allSelected ? 'success' : ''">
              <span>
                {{ `${$t('selected')}: ${selectedItemsModel.length} / ${(items).length}` }}
              </span>
            </v-chip>

            <v-chip :color="allFilteredSelected ? 'success' : ''" class="ml-1" v-if="showFilterIndicatorChip">
              <v-row no-gutters align="center">
                <v-col>
                  <span v-for="(f, index) in selectedFilterIndicators" :key="f.key" no-gutters align="center" class="flex-nowrap">
                    <v-icon x-small class="mb-1" :color="allFilteredSelected ? 'white' : 'secondary'">{{ IconHelpers.getIconForObjectType(f.key) }}</v-icon>
                    <span v-if="$vuetify.breakpoint.lgAndUp" class="ml-1">{{ $vuetify.breakpoint.xl || selectedFilterIndicators.length < 2 ? f.value : truncate(f.value, { length: 13 }) }}</span>
                    <span v-if="index < selectedFilterIndicators.length - 1 && $vuetify.breakpoint.lgAndUp" class="pr-2">,</span>
                  </span>
                </v-col>

                <v-col>
                  <span>{{ `: ${intersection(selectedItemsModel, itemsFiltered).length} / ${(itemsFiltered).length}` }}</span>
                </v-col>
              </v-row>
            </v-chip>
          </v-container>
        </template>
      </DataTable>
    </v-col>
  </v-container>
</template>

<script>
import IconHelpers from '@/utils/IconHelpers'
import { tableHeight, dataTableMinItemsPerPage } from '@/utils/componentHelpers'
import { isEqual, union, difference, intersection, truncate } from 'lodash'

export default {
  name: 'EBTractPaymentSelection',

  components: {
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    items: {
      type: Array,
      default: () => []
    },
    selectedItems: {
      type: Array,
      required: false,
      default: () => []
    },
    headers: {
      type: Array,
      required: true
    },
    tableKey: {
      type: String,
      required: true
    },
    showSelect: {
      type: Boolean,
      default: true
    },
    filterTypes: {
      type: Array,
      required: false,
      default: undefined
    },
    customCells: {
      type: Array,
      default: () => []
    }
  },

  data: () => ({
    dataTableMinItemsPerPage,
    IconHelpers,
    selectedFilter: new Map(),
    filterItems: {},
    itemsFiltered: undefined,
    allSelected: false,
    allFilteredSelected: false,
    search: null,
    selectedFilterIndicators: []
  }),

  async created () {
    this.setFilterOptions()
    this.itemsFiltered = this.items
    this.determineAllSelected(this.selectedItemsModel)
  },

  watch: {
    selectedItemsModel (v) {
      this.determineAllSelected(v)
    }
  },

  computed: {
    selectedItemsModel: {
      get () { return this.selectedItems },
      set (value) { this.$emit('update:selectedItems', value) }
    },

    tableHeight () {
      return tableHeight({
        dataLength: this.itemsFiltered?.length || this.items.length,
        offset: 500,
        headersLength: this.headers.length
      })
    },

    hasFilter () {
      return !!this.selectedFilter
    },

    iconForSelectAllBox () {
      return this.allSelected ? 'mdi-checkbox-multiple-marked'
        : this.allFilteredSelected ? 'mdi-checkbox-multiple-marked-outline'
          : this.selectedItemsModel.length > 0 ? 'mdi-minus-box-multiple'
            : 'mdi-checkbox-multiple-blank-outline'
    },

    selectAllTooltipText () {
      const viewingFiltered = this.hasFilter
      if (viewingFiltered) {
        return this.allFilteredSelected || this.allSelected
          ? this.$t('deselectAllFiltered')
          : this.$t('selectAllFiltered')
      } else {
        return this.allSelected
          ? this.$t('deselectAll')
          : this.$t('selectAll')
      }
    },

    selectAllDisabled () {
      return !!this.search
    },

    selectAllStyle () {
      return this.selectAllDisabled ? 'pointer-events: none; opacity: 0.25;' : ''
    },

    showFilterIndicatorChip () {
      return this.selectedFilterIndicators.length > 0
    },

    hasSearchText () {
      return this.search && this.search.length > 0
    },

    showFilterInfoPrependedFooter () {
      return !this.showFilterInfoReplaceFooter && !this.hasSearchText
    },

    showFilterInfoReplaceFooter () {
      const items = this.itemsFiltered ?? this.items
      return items.length < this.dataTableMinItemsPerPage && !this.hasSearchText
    }
  },

  methods: {
    intersection,
    truncate,

    setFilter (f, e) {
      if (e) {
        this.selectedFilter.set(f, e)
      } else {
        this.selectedFilter.delete(f)
      }

      this.itemsFiltered = this.items.filter(item => this.filterTypes.every(filterType => {
        const selected = this.selectedFilter.get(filterType.key)
        return selected ? item[filterType.key] === selected : true
      }))

      this.determineAllSelected(this.selectedItemsModel)

      this.selectedFilterIndicators = this.filterTypes.map(ft => ({
        key: ft.key,
        value: this.selectedFilter.get(ft.key) || ft.val
      })).filter(f => f.value !== undefined && f.value !== null && f.value !== '')
    },

    setFilterOptions () {
      if (!this.filterTypes) return
      this.filterTypes.forEach(ft => {
        this.filterItems[ft.key] = [...new Set(this.items.map(t => t[ft.key]))].filter(i => i !== null && i.length > 0).sort((a, b) => a.localeCompare(b))
      })
    },

    determineAllSelected (selectedItems) {
      const allItemIds = this.items.map(item => item[this.tableKey]).toSorted()
      const selectedItemIds = selectedItems.map(item => item[this.tableKey]).toSorted()
      this.allSelected = isEqual(allItemIds, selectedItemIds)

      if (this.selectedFilter) {
        const items = this.itemsFiltered || this.items
        const itemIds = items.map(item => item[this.tableKey])
        this.allFilteredSelected = difference(itemIds, selectedItemIds).length === 0
      } else {
        this.allFilteredSelected = false
      }
    },

    selectAllClicked () {
      const viewingFiltered = this.hasFilter
      if (viewingFiltered) {
        if (this.allFilteredSelected || this.allSelected) {
          this.selectedItemsModel = difference(this.selectedItemsModel, this.itemsFiltered)
        } else {
          this.selectedItemsModel = union(this.selectedItemsModel, this.itemsFiltered)
        }
      } else {
        if (this.allSelected) {
          this.selectedItemsModel = []
        } else {
          this.selectedItemsModel = (this.itemsFiltered || this.items)
        }
      }
    },

    searchChanged (val) {
      this.search = val
    }
  }
}
</script>
